import { platform } from '@tencent/wphk-platform-adapter';

/**
 * 获取当前页面url（兼容小程序、LiteApp、Web）
 * @returns 当前页面url
 */
function getLocationHref(): string {
  return platform.location?.href || '';
}

/**
 * 获取url指定参数的值
 * @param name url参数名
 * @param url url，默认为当前页面url
 */
export function getUrlParam(name: string, url?: string): string {
  const href = url || getLocationHref();
  if (!href) return '';
  const { searchParams } = new URL(href);
  return searchParams.get(name) ?? '';
}

/**
 * 获取URL hash部分的参数值
 * @param name 参数名
 * @param url URL字符串（默认为当前页面URL）
 * @returns 参数值（若不存在则返回空字符串）
 */
export function getUrlHash(name: string, url?: string): string {
  const href = url || getLocationHref();
  if (!href) return '';
  const { hash } = new URL(href);
  if (!hash) return '';
  const searchIndex = hash.indexOf('?');
  const search = searchIndex !== -1 ? hash.substring(searchIndex + 1) : hash.substring(1);
  const usp = new URLSearchParams(search);
  return usp.get(name) ?? '';
}

/**
 * 给指定url添加参数
 * @param url url
 * @param name key
 * @param value value
 * @returns 添加参数后的url
 */
export function addUrlParam(url: string, name: string, value: string): string {
  return addUrlParams(url, { [name]: value });
}

/**
 * 同时添加多个 URL 参数
 * @param url URL 字符串
 * @param params 需要添加的参数对象
 */
export function addUrlParams(url: string, params: Record<string, any>): string {
  const urlObject = new URL(url);
  Object.keys(params).forEach((name) => {
    urlObject.searchParams.delete(name);
    urlObject.searchParams.append(name, params[name] as string);
  });
  return urlObject.href;
}

/**
 * 去除URL中指定的参数
 * @param url url
 * @param name 参数名
 */
export function removeUrlParam(url: string, name: string) {
  return removeUrlParams(url, [name]);
}

/**
 * 移除URL中指定的参数
 * @param url URL字符串
 * @param params 需要移除的参数列表
 */
export function removeUrlParams(url: string, params: string[]): string {
  const urlObject = new URL(url);
  params.forEach((name) => {
    urlObject.searchParams.delete(name);
  });
  return urlObject.href;
}

/**
 * 将URL参数字符串转换为对象
 * @param params URL参数字符串
 */
export function paramsToObject<T extends object = {}>(params: string): T {
  const searchParams = new URLSearchParams(params);
  const result: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    result[key] = value;
  });
  return result as T;
}

/**
 * 将对象转换为URL参数字符串
 * @param object 需要转换的对象
 */
export function objectToParams(object: object): string {
  const searchParams = new URLSearchParams();
  Object.entries(object).forEach(([name, value]) => {
    searchParams.append(name, value);
  });
  return searchParams.toString();
}

/**
 * 安全解码URL参数
 * @param str 需要解码的字符串
 * @returns 解码后的字符串
 */
export function safeDecodeURIComponent(str = '') {
  try {
    return decodeURIComponent(str);
  } catch (error) {
    console.warn(error);
    return str;
  }
}

/**
 * 获取url参数中的adtag值
 * @returns adtag
 */
export function getParamAdtag() {
  const conditions = [
    'adtag',
    'ADTAG',
    'Adtag',
    'adTag',
    'hottag',
    'HOTTAG',
    'HotTag',
    'hotTag',
  ];
  for (const key of conditions) {
    const tag = getUrlParam(key) || getUrlHash(key);
    if (tag) return tag;
  }
  return '';
}

/**
 * 将对象序列化成 query string, 例如 {a: 1, b: 2} => a=1&b=2
 * @param obj 待序列化的对象
 */
export const serialize = function (obj: Record<string, any>): string {
  return Object.keys(obj)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join('&');
};
